import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../contexts/auth/Context";
import { useToast } from "../../contexts/toast/Context";
import {
  ONBOARDING_STATUS_QUERY_KEY,
  legalDocumentQueryKey,
} from "../../query/keys";
import { getOnboardingStatusRequest } from "../requests/auth";
import type { OnboardingStatus } from "../../utils/zod/auth";
import {
  STALE_LEGAL_VERSION_ERROR,
  acceptLegalDocumentsRequest,
  getLegalDocumentRequest,
} from "../requests/legal";

export const useOnboardingStatus = () => {
  const { token, authenticated } = useAuth();

  return useQuery({
    queryKey: ONBOARDING_STATUS_QUERY_KEY,
    queryFn: () => getOnboardingStatusRequest(token!),
    enabled: authenticated && !!token,
    staleTime: 0,
  });
};

/**
 * Una versión publicada es inmutable, así que se cachea para siempre por su
 * id. Cachear por tipo de documento serviría texto viejo tras publicar.
 */
export const useLegalDocumentVersion = (
  versionPublicId: string | undefined,
) => {
  const { token } = useAuth();

  return useQuery({
    queryKey: legalDocumentQueryKey(versionPublicId ?? ""),
    queryFn: () => getLegalDocumentRequest(versionPublicId!, token!),
    enabled: !!versionPublicId && !!token,
    staleTime: Infinity,
    gcTime: Infinity,
  });
};

export const useAcceptLegalDocuments = () => {
  const { token, markLegalAccepted } = useAuth();
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationFn: (documentVersionPublicIds: string[]) =>
      acceptLegalDocumentsRequest(documentVersionPublicIds, token!),
    meta: { track: true, eventName: "auth:legal-accepted" },
    onSuccess: (data) => {
      // La respuesta trae sólo la parte legal del estado: se mezcla sobre el
      // valor cacheado para no perder `mustChangePassword`, que el gate lee
      // de esta misma entrada.
      queryClient.setQueryData<OnboardingStatus>(
        ONBOARDING_STATUS_QUERY_KEY,
        (previous) => (previous ? { ...previous, ...data } : undefined),
      );
      if (!data.mustAcceptTerms) {
        markLegalAccepted();
        showToast("Documentos aceptados correctamente", "success");
      }
    },
    onError: (err: Error) => {
      // Se publicó una versión nueva con el diálogo abierto: se vuelve a
      // pedir el estado para mostrar el texto vigente, sin cerrar sesión.
      if (err.message === STALE_LEGAL_VERSION_ERROR) {
        queryClient.invalidateQueries({
          queryKey: ONBOARDING_STATUS_QUERY_KEY,
        });
        showToast(
          "El documento se actualizó mientras lo revisabas. Lee la nueva versión y acéptala.",
          "warning",
        );
        return;
      }
      showToast(err.message ?? "Error al aceptar los documentos", "error");
    },
  });

  return { mutate, isPending };
};
